import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AuthUser, Tenant, Permission, ROLE_PERMISSIONS } from '@/types/shared/auth';

interface AuthState {
  // State
  user: AuthUser | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  refreshToken: string | null;

  // Actions
  login: (user: AuthUser, tenant: Tenant, tokens: { token: string; refreshToken: string }) => void;
  logout: () => void;
  updateUser: (updates: Partial<AuthUser>) => void;
  updateTenant: (updates: Partial<Tenant>) => void;
  setLoading: (loading: boolean) => void;
  switchRole: (role: AuthUser['role']) => void; // Add this for testing different roles

  // Utilities
  hasPermission: (permission: Permission) => boolean;
  hasRole: (role: AuthUser['role'] | AuthUser['role'][]) => boolean;
  canAccess: (resource: string, action: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        tenant: null,
        isAuthenticated: false,
        isLoading: false,
        token: null,
        refreshToken: null,

        // Actions
        login: (user, tenant, tokens) => {
          // Ensure user has permissions set based on their role
          const userWithPermissions = {
            ...user,
            permissions: user.permissions || ROLE_PERMISSIONS[user.role] || [],
          };

          set({
            user: userWithPermissions,
            tenant,
            isAuthenticated: true,
            token: tokens.token,
            refreshToken: tokens.refreshToken,
            isLoading: false,
          });
        },

        logout: () => {
          set({
            user: null,
            tenant: null,
            isAuthenticated: false,
            token: null,
            refreshToken: null,
            isLoading: false,
          });
        },

        updateUser: (updates) => {
          const { user } = get();
          if (user) {
            const updatedUser = { ...user, ...updates };
            // If role is being updated, ensure permissions are updated too
            if (updates.role && !updates.permissions) {
              updatedUser.permissions = ROLE_PERMISSIONS[updates.role] || [];
            }
            set({ user: updatedUser });
          }
        },

        updateTenant: (updates) => {
          const { tenant } = get();
          if (tenant) {
            set({ tenant: { ...tenant, ...updates } });
          }
        },

        setLoading: (loading) => {
          set({ isLoading: loading });
        },

        // Dev-only. This grants the full permission set for the target role, so it is
        // strictly more powerful than the AuthContext equivalent and must be inert in
        // production. See docs/superpowers/specs/2026-08-03-terminal-j-auth-integrity.md
        switchRole: (role) => {
          if (!import.meta.env.DEV) {
            console.warn('switchRole() is disabled outside development builds.');
            return;
          }
          const { user } = get();
          if (user) {
            const updatedUser = {
              ...user,
              role,
              permissions: ROLE_PERMISSIONS[role],
            };
            set({ user: updatedUser });
          }
        },

        // Utilities
        hasPermission: (permission) => {
          const { user } = get();
          return user?.permissions?.includes(permission) ?? false;
        },

        hasRole: (role) => {
          const { user } = get();
          if (!user) return false;

          if (Array.isArray(role)) {
            // Check if user's role matches any in the array
            // This handles both exact matches and legacy role compatibility
            return role.some((r) => {
              // Direct match
              if (user.role === r) return true;

              // Legacy super_admin should match community_admin and platform roles
              if (r === 'super_admin') {
                return (
                  user.role === 'super_admin' ||
                  user.role === 'community_admin' ||
                  user.role === 'toolboxlogic_community_sp_elect' ||
                  user.role === 'toolboxlogic_community_subpanel'
                );
              }

              return false;
            });
          }

          // Single role check with legacy compatibility
          if (role === 'super_admin') {
            return (
              user.role === 'super_admin' ||
              user.role === 'community_admin' ||
              user.role === 'toolboxlogic_community_sp_elect' ||
              user.role === 'toolboxlogic_community_subpanel'
            );
          }

          return user.role === role;
        },

        canAccess: (resource, action) => {
          const { user } = get();
          if (!user || !user.permissions) return false;

          const permission = `${resource}.${action}` as Permission;
          return user.permissions.includes(permission);
        },
      }),
      {
        name: 'auth-storage-v2',
        partialize: (state) => ({
          user: state.user,
          tenant: state.tenant,
          isAuthenticated: state.isAuthenticated,
          token: state.token,
          refreshToken: state.refreshToken,
        }),
      },
    ),
    { name: 'AuthStore' },
  ),
);
